/**
 * useTabAutoSave — Debounced auto-save for active tab content.
 *
 * Calls PATCH /notes/company/{companyId}/tab/{tabId} after a debounce.
 * Shows a save-status indicator: idle → saving → saved → error.
 * Includes localStorage backup and specific error messages.
 */
import { useRef, useCallback, useState, useEffect } from 'react';
import * as notesApi from '../services/companyNotesService';

const DEBOUNCE_MS = 1200;
const LOCAL_STORAGE_KEY = 'finlytics_notes_draft';

/** Save draft to localStorage as backup */
function saveDraftToLocal(companyId, tabId, data) {
    try {
        const key = `${LOCAL_STORAGE_KEY}_${companyId}_${tabId}`;
        localStorage.setItem(key, JSON.stringify({ ...data, savedAt: Date.now() }));
    } catch (_) { /* quota exceeded etc. — ignore */ }
}

/** Remove draft from localStorage after successful server save */
function clearDraftFromLocal(companyId, tabId) {
    try {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY}_${companyId}_${tabId}`);
    } catch (_) { /* ignore */ }
}

/** Get human-readable error message based on HTTP status */
function getSaveErrorMessage(err) {
    const status = err?.response?.status;
    if (status === 401) return 'Auth error — please refresh page';
    if (status === 403) return 'Permission denied';
    if (status === 404) return 'Creating note…';
    if (status >= 500) return 'Server error — will retry';
    if (!err?.response) return 'Offline — saved locally';
    return 'Save failed';
}

export default function useTabAutoSave(companyId, companyName) {
    const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
    const [errorMsg, setErrorMsg] = useState('');
    const timerRef = useRef(null);
    const pendingRef = useRef(null);
    const mountedRef = useRef(true);
    const retryCountRef = useRef(0);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    /** Core save function used by both auto-save and manual save */
    const performSave = useCallback(async (data) => {
        if (!companyId || !data) return false;

        setSaveStatus('saving');
        setErrorMsg('');
        try {
            console.log(`[useTabAutoSave] Saving tab "${data.tabId}" for ${companyId}`);
            await notesApi.updateTabContent(companyId, data.tabId, {
                title: data.title || '',
                richContent: data.richContent || '',
                plainText: data.plainText || '',
                companyName: companyName || '',
            });
            if (mountedRef.current) {
                setSaveStatus('saved');
                retryCountRef.current = 0;
                clearDraftFromLocal(companyId, data.tabId);
                setTimeout(() => {
                    if (mountedRef.current) setSaveStatus('idle');
                }, 2000);
            }
            return true;
        } catch (err) {
            console.error('[useTabAutoSave] save error:', err?.response?.status, err?.message);
            // Always save locally as backup
            saveDraftToLocal(companyId, data.tabId, data);
            if (mountedRef.current) {
                const msg = getSaveErrorMessage(err);
                setSaveStatus('error');
                setErrorMsg(msg);
            }
            return false;
        }
    }, [companyId, companyName]);

    const scheduleSave = useCallback((tabId, { title, richContent, plainText }) => {
        if (!companyId || !tabId) return;

        // Store the latest pending data
        pendingRef.current = { tabId, title, richContent, plainText };

        // Also save a local draft immediately
        saveDraftToLocal(companyId, tabId, { tabId, title, richContent, plainText });

        // Clear existing timer
        if (timerRef.current) clearTimeout(timerRef.current);

        setSaveStatus('idle');

        timerRef.current = setTimeout(async () => {
            const data = pendingRef.current;
            if (!data) return;
            pendingRef.current = null;
            await performSave(data);
        }, DEBOUNCE_MS);
    }, [companyId, performSave]);

    /* ── Force immediate save (for manual Save button / Ctrl+S) ── */
    const flushSave = useCallback(async () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        const data = pendingRef.current;
        if (!data || !companyId) return;
        pendingRef.current = null;
        await performSave(data);
    }, [companyId, performSave]);

    /* ── Manual save (can be called from UI button even without pending data) ── */
    const manualSave = useCallback(async (tabId, content) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        // Use pending data if available, otherwise use provided content
        const data = pendingRef.current || (tabId && content ? { tabId, ...content } : null);
        if (!data) return;
        pendingRef.current = null;
        await performSave(data);
    }, [performSave]);

    return { saveStatus, errorMsg, scheduleSave, flushSave, manualSave };
}
